import { Toaster } from "@/shared/ui/toaster";
import { Toaster as Sonner } from "@/shared/ui/sonner";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { useCookieConsent } from "@/features/legal/hooks/useCookieConsent";
import { initializeGA4, disableGA4 } from "@/shared/lib/analytics";
import { initializePostHog, disablePostHog } from "@/shared/lib/posthog";
import { lazyWithRetry } from "@/shared/utils/lazyWithRetry";
const Index = lazyWithRetry(() => import("./pages/NewIndex"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
import CookieConsent from "@/features/legal/components/CookieConsent";
import MaintenanceWrapper from "@/shared/ui/MaintenanceWrapper";
import { AuthProvider } from "@/features/auth/AuthProvider";
import { RealtimeProvider } from "@/shared/contexts/RealtimeProvider";
import { AdminGuard } from "@/features/admin/components/AdminGuard";
import { ProtectedRoute } from "@/features/auth/components/ProtectedRoute";
import ErrorBoundary from "@/shared/ui/ErrorBoundary";
import { LazyLoadErrorBoundary } from "@/shared/ui/LazyLoadErrorBoundary";
import { RouteErrorBoundary } from "@/shared/ui/RouteErrorBoundary";
import { GeoBlockGate } from "@/shared/ui/GeoBlockGate";

// Lazy load non-critical routes with retry logic for failed imports
const NewDashboard = lazyWithRetry(() => import("./pages/NewDashboard"));
const OriginalsDashboard = lazyWithRetry(() => import("./pages/OriginalsDashboard"));
const OriginalsSettings = lazyWithRetry(() => import("./pages/OriginalsSettings"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const ProjectCreate = lazyWithRetry(() => import("./pages/ProjectCreate"));
const ProjectDetail = lazyWithRetry(() => import("./pages/ProjectDetail"));
const ChapterUpload = lazyWithRetry(() => import("./pages/ChapterUpload"));
const ChapterCharacterSetup = lazyWithRetry(() => import("./pages/ChapterCharacterSetup"));
const ChapterOCR = lazyWithRetry(() => import("./pages/ChapterOCR"));
const ChapterScript = lazyWithRetry(() => import("./pages/ChapterScript"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const HowToUse = lazyWithRetry(() => import("./pages/HowToUse"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Billing = lazyWithRetry(() => import("./pages/Billing"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const AdminPerformanceDashboard = lazyWithRetry(() => import("./pages/AdminPerformanceDashboard"));
const AdminOriginals = lazyWithRetry(() => import("./pages/AdminOriginals"));
const Imprint = lazyWithRetry(() => import("./pages/Imprint"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const CopyrightPolicy = lazyWithRetry(() => import("./pages/CopyrightPolicy"));
const CopyrightSubmit = lazyWithRetry(() => import("./pages/CopyrightSubmit"));
const AccountSuspended = lazyWithRetry(() => import("./pages/AccountSuspended"));
const AdminBlockedSeries = lazyWithRetry(() => import("./pages/AdminBlockedSeries"));
const ContentGuidelines = lazyWithRetry(() => import("./pages/ContentGuidelines"));
const CounterNotice = lazyWithRetry(() => import("./pages/CounterNotice"));
const AdminLaunchChecklist = lazyWithRetry(() => import("./pages/AdminLaunchChecklist"));
const About = lazyWithRetry(() => import("./pages/About"));
const Features = lazyWithRetry(() => import("./pages/Features"));
const Affiliates = lazyWithRetry(() => import("./pages/Affiliates"));
const Updates = lazyWithRetry(() => import("./pages/Updates"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const VoiceProjectCreate = lazyWithRetry(() => import("./pages/VoiceProjectCreate"));
const VoiceProjectDetail = lazyWithRetry(() => import("./pages/VoiceProjectDetail"));
const Sonic = lazyWithRetry(() => import("./pages/Sonic"));
const OriginalsStudio = lazyWithRetry(() => import("./pages/OriginalsStudio"));
const HowToOriginals = lazyWithRetry(() => import("./pages/HowToOriginals"));

// Redirect component for old speaker routes to script page
const ChapterRedirectToScript = () => {
  const { id } = useParams();
  return <Navigate to={`/chapter/${id}/script`} replace />;
};

// Hash scroll handler — scrolls to anchor on mount for hash navigation.
const HashScrollHandler = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [location.hash]);

  return null;
};

const LoadingFallback = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

/**
 * Layout for /auth — bypasses MaintenanceWrapper so users can sign in even
 * when the app is in maintenance mode (admins still need a way in).
 */
const AuthLayout = () => (
  <LazyLoadErrorBoundary>
    <RouteErrorBoundary routeName="Auth">
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
    </RouteErrorBoundary>
  </LazyLoadErrorBoundary>
);

/**
 * Default app layout — every route below this is gated by MaintenanceWrapper
 * and shares one Suspense boundary, one error boundary, and the hash-scroll
 * handler. Single routing pass per navigation.
 */
const MainLayout = () => (
  <MaintenanceWrapper>
    <LazyLoadErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <HashScrollHandler />
        <main id="main-content">
          <RouteErrorBoundary routeName="AppRoute">
            <Outlet />
          </RouteErrorBoundary>
        </main>
      </Suspense>
    </LazyLoadErrorBoundary>
  </MaintenanceWrapper>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Reasonable defaults for an app where most data is per-user and
      // doesn't change tab-to-tab. Individual hooks can override.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  const { canUseAnalytics } = useCookieConsent();

  // Defer GA4 initialization to improve initial page load.
  // ID is sourced from env (VITE_GA4_MEASUREMENT_ID) with a hardcoded
  // fallback so existing deployments keep working without env changes.
  useEffect(() => {
    const GA4_MEASUREMENT_ID =
      (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined) ??
      "G-RSVQR4TB5P";

    const loadAnalytics = () => {
      const consented = canUseAnalytics();

      if (consented) {
        initializePostHog(true);
      } else {
        disablePostHog();
      }

      if (!GA4_MEASUREMENT_ID) {
        disableGA4();
        return;
      }
      if (consented) {
        initializeGA4(GA4_MEASUREMENT_ID, true);
      } else {
        disableGA4();
      }
    };

    if ("requestIdleCallback" in window) {
      requestIdleCallback(loadAnalytics);
    } else {
      setTimeout(loadAnalytics, 2000);
    }
  }, [canUseAnalytics]);

  return (
    <ErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="recapmanga-theme">
        <GeoBlockGate>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AuthProvider>
                <RealtimeProvider>
                  {/* Skip Navigation Link for Accessibility */}
                  <a
                    href="#main-content"
                    className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-background focus:text-foreground focus:border focus:border-ring focus:rounded-md focus:shadow-lg"
                  >
                    Skip to main content
                  </a>
                  <CookieConsent />
                  <Routes>
                    {/* /auth bypasses MaintenanceWrapper */}
                    <Route element={<AuthLayout />}>
                      <Route path="/auth" element={<Auth />} />
                    </Route>

                    {/* Everything else goes through MaintenanceWrapper */}
                    <Route element={<MainLayout />}>
                      <Route path="/" element={<Index />} />
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <NewDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/originals"
                        element={
                          <ProtectedRoute>
                            <OriginalsDashboard />
                          </ProtectedRoute>
                        }
                      />
                      {/* The studio's own account page — Ink, its ledger, and
                          the account itself. Same guards as the studio. */}
                      <Route
                        path="/originals/settings"
                        element={
                          <ProtectedRoute>
                              <OriginalsSettings />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/account-suspended" element={<AccountSuspended />} />
                      <Route path="/signin" element={<Navigate to="/auth" replace />} />

                      <Route
                        path="/project/new"
                        element={
                          <ProtectedRoute>
                            <ProjectCreate />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/project/:id"
                        element={
                          <ProtectedRoute>
                            <ProjectDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/project/:id/chapter/new"
                        element={
                          <ProtectedRoute>
                            <ChapterUpload />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/chapter/:id/character-setup"
                        element={
                          <ProtectedRoute>
                            <ChapterCharacterSetup />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/chapter/:id/ocr"
                        element={
                          <ProtectedRoute>
                            <ChapterOCR />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/chapter/:id/speakers" element={<ChapterRedirectToScript />} />
                      <Route
                        path="/chapter/:id/script"
                        element={
                          <ProtectedRoute>
                            <ChapterScript />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/payment-success"
                        element={
                          <ProtectedRoute>
                            <PaymentSuccess />
                          </ProtectedRoute>
                        }
                      />

                      {/* Legal & marketing */}
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/legal/copyright-policy" element={<CopyrightPolicy />} />
                      <Route path="/legal/copyright-submit" element={<CopyrightSubmit />} />
                      {/* Redirects for old DMCA URLs */}
                      <Route path="/legal/dmca" element={<Navigate to="/legal/copyright-policy" replace />} />
                      <Route
                        path="/legal/dmca-submit"
                        element={<Navigate to="/legal/copyright-submit" replace />}
                      />
                      {/* Redirects for old privacy/terms URLs */}
                      <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
                      <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
                      <Route path="/legal/privacy" element={<Navigate to="/privacy" replace />} />
                      <Route path="/legal/terms" element={<Navigate to="/terms" replace />} />
                      <Route path="/supported-content" element={<Navigate to="/legal/content-guidelines#supported-content" replace />} />
                      <Route path="/legal/counter-notice" element={<CounterNotice />} />
                      <Route path="/legal/content-guidelines" element={<ContentGuidelines />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/features" element={<Features />} />
                      <Route path="/affiliates" element={<Affiliates />} />
                      <Route path="/updates" element={<Updates />} />
                      <Route path="/changelog" element={<Navigate to="/updates" replace />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                      <Route path="/how-to-use" element={<HowToUse />} />
                      <Route path="/sonic" element={<Sonic />} />
                      {/* Public marketing page for the Originals product; the
                          app itself lives at /originals (auth-gated). */}
                      <Route path="/originals-studio" element={<OriginalsStudio />} />
                      <Route path="/how-to-originals" element={<HowToOriginals />} />
                      <Route path="/imprint" element={<Imprint />} />

                      <Route
                        path="/voice/new"
                        element={
                          <ProtectedRoute>
                            <VoiceProjectCreate />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/voice/:id"
                        element={
                          <ProtectedRoute>
                            <VoiceProjectDetail />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/settings"
                        element={
                          <ProtectedRoute>
                            <Settings />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings/billing"
                        element={
                          <ProtectedRoute>
                            <Billing />
                          </ProtectedRoute>
                        }
                      />

                      {/* Admin */}
                      <Route
                        path="/admin"
                        element={
                          <AdminGuard>
                            <AdminDashboard />
                          </AdminGuard>
                        }
                      />
                      <Route
                        path="/admin/performance"
                        element={
                          <AdminGuard>
                            <AdminPerformanceDashboard />
                          </AdminGuard>
                        }
                      />
                      <Route
                        path="/admin/originals"
                        element={
                          <AdminGuard>
                            <AdminOriginals />
                          </AdminGuard>
                        }
                      />
                      <Route
                        path="/admin/blocked-series"
                        element={
                          <AdminGuard>
                            <AdminBlockedSeries />
                          </AdminGuard>
                        }
                      />
                      <Route
                        path="/admin/launch-checklist"
                        element={
                          <AdminGuard>
                            <AdminLaunchChecklist />
                          </AdminGuard>
                        }
                      />

                      <Route path="*" element={<NotFound />} />
                    </Route>
                  </Routes>
                </RealtimeProvider>
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
        </GeoBlockGate>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
